/**
 * paperSlice.ts
 * 论文相关的 Zustand 状态管理切片
 * 包含论文的增删改查、AI列管理、上传、元数据、进度等核心业务
 * 适用于 findin-web 项目
 */
import axios from 'axios';
import { DeepCamelToSnake, WrapResponse } from '@/types';
import { StateSlice } from '@/stores/appStore';
import { getCurrentTime, snakeToCamel, transferCamelToSnake } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { DEFAULT_MAX_PAGE_SIZE, DEFAULT_PAGE_SIZE, MAX_SIZE_M } from '@/config/const';
import { Coordinate, Tools } from './chatSlice';
import { api, host } from '@/lib/env';
import {
  getFileInfo,
  colDataToCol,
  bindUploadByHash,
} from '@/stores/slices/lib/paperUtils';
import type { FileInfo, Col, ColData, PaperFileData, AiColLang } from './lib/paperTypes';
import { SubjectId } from './subjectSlice';
import { fun } from '@/lib/request';
import { CslItem } from '@/hooks/useCiteproc';
export type { FileInfo, Col, ColData, PaperFileData } from './lib/paperTypes';

export type ID = string;
export type PaperId = ID;
// 0待处理，1处理中，2完成，-1失败
export type PaperParseStatus = 0 | 1 | 2 | -1 | number;
export type ExtractImage = {
  caption: string;
  imagePath: string;
  imagePage?: string;
}
export type Understand = {
  key: string;
  status: PaperParseStatus;
  result: string;
  title?: string;
  images?: ExtractImage[];
  tables?: ExtractImage[];
  taskId: string;
};
export type PaperImage = {
  url: string;
  size: number;
  width: number;
  height: number;
} & Coordinate;

export type PaperExtra = {
  author: string; // 多个 author 用逗号隔开
  title: string; // 标题
  titleZh: string; // 中文标题
  titleEn: string; // 英文标题
  organization: string; // 所属机构
  createAt: string; // 编写时间
  journal: string; // 期刊信息
  doi: string;
  url: string; // doi 对应的 url
  citedByCount: number; // 被引用次数
  issue: string;
  pages: string;
  school: string;
  type: string;
  volume: string;
  year: string;
};

export type PaperMetadata = Omit<PaperExtra, 'titleZh' | 'titleEn' | 'createAt'> & {
  url: string;
}

export type PaperMetadataData = PaperMetadata & {
  author: string[];
}

export type SavePaperMetadataOptions = {
  paperId: string;
  metadata: PaperMetadata | { year: string | number; }
}

export type Information = {
  key: 'information';
  status: PaperParseStatus;
  result: PaperExtra;
}

export type Paper = {
  paperId: PaperId;
  paperName: string;
  createTime: string;
  /** 置顶时间；为空表示未置顶。来自 /paper/list 的 pin_time。 */
  pinTime?: string | null;
  resourceUrl: string;
  // 是否有摘要
  hasAbstract: boolean;
  // PDF 文件 URL（原 resource_url），用于判断是否存在 PDF 文件
  pdfUrl?: string;
  // 附件 ID，attachment_id === 0 代表是摘要型文献
  attachmentId?: string | number;
  subjects: {
    id: string;
    subjectName: string;
  }[];
  noteCount: number;
  understands?: {
    // summarize, research_method, ...
    [key: string]: Understand;
  };
  information: Information;
  chatId?: ID;
  tools?: Tools;
  currPage: number;
  totalPage: number;
  references?: {
    name: string;
    author: string;
    date: string;
    links: {
      text: string;
      url: string;
    }[];
    orgText: string;
  }[];
  /**
   * -2，未提交任务
   * -1，生成失败
   * 0，待生成
   * 1，生产中
   * 2，完成
   */
  referencesStatus?: number;
  // -1，-2 不支持解析该类型文献，2
  extractStatus: number;
  extractImage: number;
  extra: PaperExtra;
  images: PaperImage[];
  citationFormat?: string;
  // 替代citationFormat，用于citeproc-js格式化引用文献
  citeprocData: CslItem;
  // 初始化知识库
  // 0：未初始化；1：初始化中；2：初始化完成
  // -1：知识库生成失败，无法进行文献问答
  initKnowledge?: number;
  // 小于 0，文献解析失败
  extractContent: number;
  // 失败时的文案
  error: string;
  // 文献引用是否引用
  quote: Boolean;
  // 删除
  isDeleted: number;
  titleChinese: boolean; // 是否中文标题
  titleZh: string; // 中文标题
  titleEn: string; // 英文标题
  // 标题
  title: string;
  // 摘要
  abstract: string;
  // 作者们
  author: string;
  // 发表时间
  createAt: string;
  // url
  url: string;
  // links
  links: {
    text: string;
    url: string;
  }[];

};

// 创建 AI 列
export type CreateAiColOptions = {
  colName: string;
  colDesc: string;
  lang: AiColLang;
}

export type PreCreateAiColPayload = {
  [key: string]: unknown;
} & CreateAiColOptions;

export type PreCreateAiColResult = {
  colName: string;
  colDesc: string;
  lang: AiColLang;
  requirement: string;
  requirementShow: string;
  [key: string]: unknown;
}

export type CreateAiColPayload = {
  [key: string]: unknown;
} & Partial<PreCreateAiColResult>;

export type DeleteAiColOptions = {
  colId: number;
}

// AI 列飞豆价格
export type ColConsumePrice = {
  stdCount: number;
  stdConsume: number;
  stdUnitPrice: number;
  customUnitPrice: number;
  customCount: number;
  customConsume: number;
  chartUnitPrice: number;
  chartCount: number;
  chartConsume: number;
  userBalance: number;
} | null;

export type FetchPaperMode = 'base';
export type FetchPaperOrder = 'title,asc' | 'title,desc'
  | 'create_time,asc' | 'create_time,desc' | 'year,desc' | 'year,asc';
export type FetchPapersParams = {
  skipGlobalUpdate?: boolean; // 当为 true 时，不更新全局缓存（仅用于 mode === 'base'）
  subjectId?: string;
  mode?: FetchPaperMode;
  pageSize?: number;
  pageIndex?: number;
  order?: FetchPaperOrder;
  search?: string;
}

/** list 接口返回的当前引用格式（与 citation_styles 配置接口结构一致） */
export type PapersCitationStyle = {
  currStyle?:
    | {
      styleKey?: string;
    }
    | string;
  styles?: Record<string, {
    display: string;
    csl?: string;
    style?: string;
  }>;
};

/** 从 citationStyle 块读取 currStyle.styleKey（兼容驼峰 / 下划线） */
export function getListCitationStyleApiKey(citationStyle?: PapersCitationStyle | null) {
  if (!citationStyle) return null;

  const loose = citationStyle as Record<string, unknown>;
  const rootKey = loose.styleKey ?? loose.style_key;
  if (typeof rootKey === 'string' && rootKey.trim()) {
    return rootKey.trim();
  }

  const curr = citationStyle.currStyle ?? loose.curr_style;
  if (!curr) return null;

  if (typeof curr === 'string') {
    const trimmed = curr.trim();
    return trimmed || null;
  }

  if (typeof curr === 'object') {
    const currLoose = curr as Record<string, unknown>;
    const key = currLoose.styleKey ?? currLoose.style_key;
    if (typeof key === 'string' && key.trim()) {
      return key.trim();
    }
  }

  return null;
}

/**
 * 从 list 接口 payload 提取当前引用 style_key。
 * 支持已是驼峰的 `citationStyle.currStyle.styleKey`，以及 snake_case 字段。
 */
export function extractListCitationStyleKey(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const root = payload as Record<string, unknown>;

  const directKey = root.citationStyleKey ?? root.citation_style_key;
  if (typeof directKey === 'string' && directKey.trim()) {
    return directKey.trim();
  }

  const block = root.citationStyle ?? root.citation_style;
  if (!block || typeof block !== 'object') return null;

  const fromBlock = getListCitationStyleApiKey(block as PapersCitationStyle);
  if (fromBlock) return fromBlock;

  const normalized = snakeToCamel<{ citationStyle: PapersCitationStyle }>(
    { citation_style: block } as DeepCamelToSnake<{ citationStyle: PapersCitationStyle }>,
  ).citationStyle as PapersCitationStyle;
  return getListCitationStyleApiKey(normalized);
}

export type PapersData = {
  papers: Paper[];
  cols: Col[];
  total: number;
  // 每页数量
  pageSize: number;
  // 当前页码
  pageIndex: number;
  // search
  search: string;
  // order
  order: FetchPaperOrder;
  /** 当前主题/列表的引用格式，来自 POST /paper/list */
  citationStyle?: PapersCitationStyle | null;
  /** list 当前引用 style_key（扁平字段，便于可靠读取） */
  citationStyleKey?: string | null;
  /** 本次 list 响应是否包含 citation_style 字段 */
  citationStyleFromListResponse?: boolean;
  /** 当前范围内置顶数量上限，来自 POST /paper/list 的 pin_limit */
  pinLimit?: number | null;
  /** 当前范围内已置顶数量，来自 POST /paper/list 的 pinned_count */
  pinnedCount?: number | null;
};

// 服务端数据
type FetchPapersData = {
  // AI 列
  paperAiCol: ColData[];
  // 数据
  list: Paper[];
  // 总量
  total: number;
  // 每页数量
  pageSize: number;
  // 当前页码，从 1 开始
  pageIndex: number;
  citationStyle?: PapersCitationStyle;
  pinLimit?: number | null;
  pinnedCount?: number | null;
};

export type Pagination = {
  setPageSize: (pageSize: number, subjectId?: string) => void;
  setPageIndex: (pageIndex: number, subjectId?: string) => void;
  previousPage: (subjectId?: string) => void;
  getCanPreviousPage: (subjectId?: string) => boolean;
  nextPage: (subjectId?: string) => void;
  getCanNextPage: (subjectId?: string) => boolean;
  getPageCount: (subjectId?: string) => number;
}

export type ExportPapersFormat = 'none' | 'plus';
export type ExportPapersFileType = 'excel' | 'word';
export type ExportPapersParams = {
  colKeys: string[];
  paperIds: PaperId[];
  subjectId: SubjectId;
  format: ExportPapersFormat;
  fileType: ExportPapersFileType;
}

export type SetPaperColsMode = 'add' | 'del';

// 0 未请求；-1 请求被中断未获取到数据；1 请求到数据；
export type PapersFetchedStatus = -1 | 0 | 1;

type UploadTaskResult = {
  // 0  正常上传；
  // -2 上传失败，提示「文献上传失败，请重试」；
  // -3 重复上传，批次内重复，提示「重复文献」；
  // -4 文献已存在（userExisted === true 且 subjectId 为空），提示「重复文献」；
  // -5 文献在「我的文献」中已存在、在当前主题中不存在（userExisted === true 且 subjectExisted === false 且 subjectId 不为空），提示「重复文献，已同步当前主题」；
  // -6 文献在「我的文献」中已存在、在当前主题中也存在（userExisted === true 且 subjectExisted === true 且 subjectId 不为空），提示「重复文献，已在当前主题内」；
  // -7 文献已在「已导入列表」中（前端 uploadedIds 判断），提示「重复文献，已在清单内」；
  // -10 超大文件，提示「文献超出大小限制」；
  // 其他：服务端直出错误码
  code: number;
  message?: string;
  paper?: PaperFileData | null;
  isDuplicate?: boolean;
};

export type UploadAllTasksCompleteParams = {
  balanceInsufficient: boolean;
  uploadLimitReached: boolean;
};

export type UploadTaskCallbacks = {
  onTaskStart?: (taskId: string, file: File) => void;
  onProgress?: (taskId: string, progress: number) => void;
  onTaskComplete?: (taskId: string, result: UploadTaskResult) => void;
  /** 整批上传全部完成后调用一次（用于 toast / 升级窗等只弹一次的逻辑） */
  onAllTasksComplete?: (params: UploadAllTasksCompleteParams) => void;
};

export type UploadPapersOptions = {
  files: File[];
  subjectId?: string;
  ignoreAiCol?: boolean;
  filesInfo?: FileInfo[];
  taskIds?: string[];
  callbacks?: UploadTaskCallbacks;
};

export type UploadExistDataItem = {
  existed: boolean; // 是否已存在 OSS 上
  userExisted: boolean; // 是否已存在当前用户文献库中
  subjectExisted: boolean; // 是否已存在当前主题文献库中
  uploadUrl: string; // OSS 上传 URL
};

const initialPapersData = () => ({
  papers: [],
  cols: [],
  total: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  pageIndex: 0,
  search: '',
  order: 'create_time,desc' as FetchPaperOrder,
  pinLimit: null,
  pinnedCount: null,
})
export type PaperSlice = {
  // 当前列表数据
  papers: Paper[];
  // 整体列表数据，根据subjectId索引
  papersData: Record<string, PapersData>;
  /** list 接口解析到的引用 style_key，避免并发 list 覆盖 papersData.citationStyleKey */
  listCitationStyleKeys: Record<string, string>;
  /** 接口返回了 citation_style 块但无 style_key，UI 使用默认 GB/T */
  listCitationStyleUseDefault: Record<string, boolean>;
  getPapersData: (subjectId?: string) => PapersData;
  initPapersData: (subjectId?: string) => void;
  filters: {
    setSearch: (search: string, subjectId?: string) => void;
    setOrder: (order: FetchPaperOrder, subjectId: string) => void;
  },
  pagination: Pagination,
  getPagination: (subjectId?: string) => Pagination;
  fetchingPaperId: PaperId | '';
  fetchPaperLoading: boolean;
  fetchPaper: (paperId: PaperId, silent?: boolean) => Promise<Paper | null>;
  // 获取PaperDetail
  requestPaper: (paperId: PaperId) => Promise<Paper | null>;
  fetchPapersLoading: boolean;
  setFetchPapersLoading: (loading: boolean) => void;
  paperRecords: Record<PaperId, Paper>;
  // 0 未请求；-1 请求被中断未获取到数据；1 请求到数据；
  papersFetchedStatus: Record<PaperId, PapersFetchedStatus>;
  getPapersFetchedStatus: (subjectId?: string) => PapersFetchedStatus;
  fetchPapersSilent: boolean;
  fetchPapers: (params?: FetchPapersParams | string, silent?: boolean) => Promise<Paper[] | undefined>;
  uploadPapers: (options: UploadPapersOptions) => Promise<void>;
  // renamePaper: (paperId: PaperId, newTitle: string) => Promise<boolean>;
  deletePaper: (paperId: PaperId, subjectId?: string) => Promise<boolean>;
  deletePapers: (paperIds: PaperId[], subjectId?: string) => Promise<boolean>;
  fetchPaperCols: (subjectId?: string) => Promise<void>;
  setCitationStyle: (styleKey: string) => Promise<boolean>;
  setPaperCols: (cols: string[], subjectId?: string, mode?: SetPaperColsMode) => Promise<void>;
  preCreateAiCol: (options: PreCreateAiColPayload) => Promise<PreCreateAiColResult | null>;
  createAiCol: (options: CreateAiColPayload, subjectId?: string) => Promise<void>;
  deleteAiCol: (options: DeleteAiColOptions, subjectId?: string) => Promise<void>;
  colConsumePrice: ColConsumePrice;
  setColConsumePrice: (col: ColConsumePrice) => void;
  refreshPaperCol: (col: string, subjectId?: string, paperId?: string, mode?: string) => Promise<void | ColConsumePrice>;
  referencesExplain: (paperId: PaperId) => Promise<void>;
  refreshPaper: (paperId: PaperId) => Promise<void>;
  updateReadingProgress: (params: {
    paperId: PaperId;
    currPage: string;
    totalPage: string;
  }) => Promise<void>;
  // 通过 doi 获取
  fetchPaperMetadataLoading: boolean;
  clearCurrentPaperMetadataByDoi: () => void;
  currentPaperMetadataByDoi: PaperMetadata | null;
  currentPaperMetadataByDoiError: string;
  fetchPaperMetadataByDoi: (options: {
    doi: string;
  }) => Promise<void>;
  savePaperMetadata: (options: SavePaperMetadataOptions) => Promise<boolean>;
  exportPapers: (params: ExportPapersParams) => Promise<{
    filename: string;
    data: any;
  } | null>;
};

export const COMMON = 'COMMON';
export const createPaperSlice: StateSlice<PaperSlice> = ((set, get) => {
  const pid = (subjectId?: string) => (subjectId || COMMON);
  const paginationData: Record<string, Pagination> = {};
  // 为不同 subjectId / mode 维护独立的中止控制器，避免相互中断
  const fetchPapersAbortControllers: Record<string, AbortController | null> = {};
  // 并发 list 请求时，仅最新一代响应写入 store（避免 abort 后丢弃含 citation_style 的响应）
  const fetchPapersRequestGen: Record<string, number> = {};
  /** 默认 list 首屏请求合并为同一 Promise，避免早退返回空 citation 缓存 */
  const fetchPapersInFlight: Partial<Record<string, Promise<Paper[] | undefined>>> = {};
  // 缓存正在进行的 fetchPaper 请求，防止重复请求
  const fetchPaperPromises: Record<string, Promise<Paper | null>> = {};

  const markListCitationStyleUseDefault = (subjectId: string | undefined) => {
    const storeKey = pid(subjectId);
    set((state) => {
      state.listCitationStyleUseDefault[storeKey] = true;
    });
  };

  const persistListCitationStyleKey = (subjectId: string | undefined, key: string | null | undefined) => {
    const trimmed = typeof key === 'string' ? key.trim() : '';
    if (!trimmed) return;
    const storeKey = pid(subjectId);
    set((state) => {
      state.listCitationStyleKeys[storeKey] = trimmed;
      delete state.listCitationStyleUseDefault[storeKey];
    });
  };

  return ({
    papersData: {
      [COMMON]: initialPapersData(),
    },
    listCitationStyleKeys: {},
    listCitationStyleUseDefault: {},
    // base 模式数据存在这里
    papers: [],
    fetchingPaperId: '',
    fetchPaperLoading: false,
    paperRecords: {},
    papersFetchedStatus: {},
    getPapersFetchedStatus: (subjectId) => {
      return get().papersFetchedStatus[pid(subjectId)] ?? 0;
    },
    getPapersData: (subjectId) => {
      const key = pid(subjectId);
      const data = get().papersData[key];
      // 在渲染期间返回默认值，不更新状态
      if (!data) {
        return initialPapersData();
      }
      return data;
    },
    initPapersData: (subjectId) => {
      const key = pid(subjectId);
      if (!get().papersData[key]) {
        set((state) => {
          state.papersData[key] = initialPapersData();
        });
      }
    },
    fetchPapersLoading: false,
    setFetchPapersLoading: (loading: boolean) => {
      set((state) => {
        state.fetchPapersLoading = loading;
      });
    },
    fetchPapersSilent: false,
    fetchPapers: async (_params = '', silent = false) => {
      let params = {
        subjectId: '',
      } as FetchPapersParams;
      if (typeof _params === 'string') {
        params.subjectId = _params;
      } else {
        params = {
          ...params,
          ..._params,
        };
      }
      const subjectId = params.subjectId;
      const modeKey = params.mode === 'base' ? 'base' : 'list';
      const ctrlKey = `${pid(subjectId)}:${modeKey}`;
      const shouldCoalesceListFetch =
        !subjectId &&
        modeKey === 'list' &&
        (params.pageIndex ?? 0) === 0 &&
        (params.search ?? '') === '';

      const coalescedListFetch = shouldCoalesceListFetch
        ? fetchPapersInFlight[ctrlKey]
        : undefined;
      if (coalescedListFetch) {
        return coalescedListFetch;
      }

      // 如果是全局（subjectId 为空）且 mode=base 的列表请求，并且已经有同 key 的请求在进行中，
      // 直接复用当前列表数据，避免重复请求和中断前一个请求
      if (!subjectId && modeKey === 'base' && fetchPapersAbortControllers[ctrlKey]) {
        return get().papers ?? [];
      }

      const executeFetch = async (): Promise<Paper[] | undefined> => {
      // 仅取消同 key 的未完成请求，互不影响（全局 base 模式和默认 list 视图在上面 return，不会走到这里）
      if (fetchPapersAbortControllers[ctrlKey]) {
        fetchPapersAbortControllers[ctrlKey]?.abort();
        fetchPapersAbortControllers[ctrlKey] = null;
      }

      const papaersData = get().getPapersData(subjectId);
      // 非mode=base，分页，使用的页码
      if (params.mode !== 'base') {
        params.pageIndex = params.pageIndex ?? 0;
        params.pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
        params.search = params.search ?? papaersData.search;
        params.order = params.order ?? papaersData.order;
      }

      // 创建新的AbortController（按 key 隔离）
      const currentAbortController = new AbortController();
      fetchPapersAbortControllers[ctrlKey] = currentAbortController;
      const requestGen = (fetchPapersRequestGen[ctrlKey] ?? 0) + 1;
      fetchPapersRequestGen[ctrlKey] = requestGen;
      set(state => {
        if (!silent) {
          state.fetchPapersLoading = true;
        }
      });
      let fetchedData: PapersData | null = null;
      try {
        const data = await requestPapers({
          ...params,
          signal: currentAbortController.signal // 传递中止信号
        }, get().getPapersData(subjectId));

        if (data?.citationStyleKey) {
          persistListCitationStyleKey(subjectId, data.citationStyleKey);
        } else if (
          data?.citationStyleFromListResponse &&
          !get().listCitationStyleKeys[pid(subjectId)]
        ) {
          markListCitationStyleUseDefault(subjectId);
        }

        // 已有更新的同 key 请求：整表写入跳过，但含 citation_style 的过期响应仍合并样式
        if (fetchPapersRequestGen[ctrlKey] !== requestGen) {
          if (data && params.mode !== 'base' && (data.citationStyleKey || data.citationStyle)) {
            set((state) => {
              const prev = state.papersData[pid(subjectId)];
              if (!prev) return;
              state.papersData[pid(subjectId)] = {
                ...prev,
                citationStyleKey: data.citationStyleKey ?? prev.citationStyleKey,
                citationStyle: data.citationStyle ?? prev.citationStyle,
              };
            });
          }
          return;
        }
        if (data) {
          fetchedData = data;
          set((state) => {
            const cols = state.papersData[pid(subjectId)]?.cols;
            if (params.mode === 'base') {
              // 如果 skipGlobalUpdate 为 true，不更新全局缓存
              if (!params.skipGlobalUpdate) {
                state.papers = data.papers;
              }
            } else {
              // 只有第一次获取cols，后面不需要
              const prev = state.papersData[pid(subjectId)];
              const storeKey = pid(subjectId);
              const persistedKey = state.listCitationStyleKeys[storeKey];
              state.papersData[storeKey] = {
                ...data,
                cols: cols.length ? cols : data.cols,
                citationStyle: data.citationStyle ?? prev?.citationStyle ?? null,
                citationStyleKey: data.citationStyleKey ?? persistedKey ?? prev?.citationStyleKey ?? null,
              };
            }
            state.fetchPapersSilent = !!silent;
          });
        }
      } catch (error) {
        if (isAbortError(error)) {
          if (fetchPapersRequestGen[ctrlKey] === requestGen) {
            set((state) => {
              if (!state.papersFetchedStatus[pid(subjectId)]) {
                // 此次请求中断未获取到数据
                state.papersFetchedStatus[pid(subjectId)] = -1;
              }
            });
          }
          return;
        }
      } finally {
        set(state => {
          if (!silent) {
            state.fetchPapersLoading = false;
          }
        });
        // 仅清理当前 key 的控制器
        if (fetchPapersAbortControllers[ctrlKey] === currentAbortController) {
          fetchPapersAbortControllers[ctrlKey] = null;
        }
      }
      if (fetchPapersRequestGen[ctrlKey] === requestGen) {
        set((state) => {
          state.papersFetchedStatus[pid(subjectId)] = 1;
        });
      }
      if (params.mode === 'base') {
        // 如果 skipGlobalUpdate 为 true，返回请求的数据而不是全局缓存
        if (params.skipGlobalUpdate && fetchedData) {
          return fetchedData.papers ?? [];
        }
        return get().papers ?? [];
      } else {
        return get().getPapersData(subjectId).papers ?? [];
      }
      };

      if (shouldCoalesceListFetch) {
        let inFlight = fetchPapersInFlight[ctrlKey];
        if (!inFlight) {
          inFlight = executeFetch();
          fetchPapersInFlight[ctrlKey] = inFlight;
          void inFlight.finally(() => {
            if (fetchPapersInFlight[ctrlKey] === inFlight) {
              delete fetchPapersInFlight[ctrlKey];
            }
          });
        }
        return inFlight;
      }

      return executeFetch();
    },
    filters: {
      setSearch: (search: string, subjectId?: string) => {
        get().initPapersData(subjectId);
        set((state) => {
          state.papersData[pid(subjectId)].search = search;
        });
      },
      setOrder: (order: FetchPaperOrder, subjectId: string = '') => {
        get().initPapersData(subjectId);
        set((state) => {
          state.papersData[pid(subjectId)].order = order;
        });
      },
    },
    pagination: {
      setPageSize: (_pageSize: number, subjectId: string = '') => {
        get().initPapersData(subjectId);
        const pageSize = Math.min(Math.max(_pageSize, 1), DEFAULT_MAX_PAGE_SIZE);
        set((state) => {
          state.papersData[pid(subjectId)].pageSize = pageSize;
        });
        // 调整每页数量，回到第一页
        get().fetchPapers({
          subjectId,
          pageIndex: 0,
          pageSize,
        });
      },
      setPageIndex: (_pageIndex: number, subjectId: string = '') => {
        get().initPapersData(subjectId);
        const { pageSize, total, pageIndex: currentIndex } = get().getPapersData(subjectId);
        // 0 ~ length -1
        const maxPageIndex = Math.ceil(total / pageSize) - 1;
        const pageIndex = Math.min(Math.max(_pageIndex, 0), maxPageIndex);
        // 若页码未变化则不触发请求
        if (pageIndex === currentIndex) return;
        set((state) => {
          state.papersData[pid(subjectId)].pageIndex = pageIndex;
        });
        // 调整页码
        get().fetchPapers({
          subjectId,
          pageIndex,
          pageSize,
        });
      },
      previousPage: (subjectId?: string) => {
        const { pageIndex } = get().getPapersData(subjectId);
        if (get().pagination.getCanPreviousPage(subjectId)) {
          get().pagination.setPageIndex(pageIndex - 1, subjectId);
        }
      },
      getCanPreviousPage: (subjectId?: string) => {
        const { pageIndex } = get().getPapersData(subjectId);
        return pageIndex > 0;
      },
      nextPage: (subjectId?: string) => {
        const { pageIndex } = get().getPapersData(subjectId);
        if (get().pagination.getCanNextPage(subjectId)) {
          get().pagination.setPageIndex(pageIndex + 1, subjectId);
        }
      },
      getCanNextPage: (subjectId?: string) => {
        const { pageIndex, total, pageSize } = get().getPapersData(subjectId);
        return pageIndex < Math.ceil(total / pageSize) - 1;
      },
      getPageCount: (subjectId?: string) => {
        const { pageSize, total } = get().getPapersData(subjectId);
        return Math.ceil(total / pageSize);
      },
    },
    // 给出一个当前subjectId的操作器，所有方法可以不需要再传subjectId
    getPagination(subjectId: string = '') {
      if (!paginationData[pid(subjectId)]) {
        const { pagination } = get();
        paginationData[pid(subjectId)] = {
          setPageSize: (pageSize) => pagination.setPageSize(pageSize, subjectId),
          setPageIndex: (pageIndex) => pagination.setPageIndex(pageIndex, subjectId),
          previousPage: () => pagination.previousPage(subjectId),
          getCanPreviousPage: () => pagination.getCanPreviousPage(subjectId),
          nextPage: () => pagination.nextPage(subjectId),
          getCanNextPage: () => pagination.getCanNextPage(subjectId),
          getPageCount: () => pagination.getPageCount(subjectId),
        }
      }
      return paginationData[pid(subjectId)];
    },
    fetchPaper: async (paperId, silent = false) => {
      const paperIdStr = paperId.toString();

      // 如果已经有正在进行的相同请求，直接返回该 Promise
      const existingPromise = fetchPaperPromises[paperIdStr];
      if (existingPromise) {
        return existingPromise;
      }

      // 创建新的请求 Promise
      const fetchPromise = (async () => {
        set(state => {
          if (!silent) {
            state.fetchPaperLoading = true;
          }
          state.fetchingPaperId = paperId;
        });
        try {
          const formData = new FormData();
          formData.append('paper_id', paperIdStr);
          const { data, code, message } = (
            await fun.post<WrapResponse<Paper>>(
              api('/paper/detail', true),
              formData,
            )
          ).data;
          const paperData = snakeToCamel<Paper>(data);
          if (code === 0) {
            const current = get().papers.find((paper) => (
              paper.paperId.toString() === paperIdStr
            )) || {};
            const paper = {
              ...current,
              ...get().paperRecords[paperId],
              ...paperData,
            };
            set((state) => {
              state.paperRecords[paperId] = paper;
            });
            return paper;
          } else if (code !== 4002) {
            // 4002 论文不存在
            toast.error(message);
          }
          return null;
        } catch (error) {
          console.error('Error fetch paper:', error);
          return null;
        } finally {
          set(state => {
            state.fetchPaperLoading = false;
            state.fetchingPaperId = '';
          });
          // 请求完成后清除缓存
          delete fetchPaperPromises[paperIdStr];
        }
      })();

      // 缓存该 Promise
      fetchPaperPromises[paperIdStr] = fetchPromise;
      return fetchPromise;
    },
    requestPaper: async (paperId) => {
      try {
        const formData = new FormData();
        formData.append('paper_id', paperId.toString());
        const { data, code, message } = (
          await fun.post<WrapResponse<Paper>>(
            api('/paper/detail', true),
            formData,
          )
        ).data;
        const paperData = snakeToCamel<Paper>(data, {
          modifier: (v, k) => (k?.endsWith('id') ? v.toString() : v),
        });
        if (code === 0) {
          return paperData;
        } else if (code !== 4002) {
          // 4002 论文不存在
          toast.error(message);
        }
        return null;
      } catch (error) {
        console.error('Error uploading file:', error);
        return null;
      } finally {
      }
    },
    uploadPapers: async ({
      files,
      subjectId = '',
      ignoreAiCol,
      filesInfo,
      taskIds = [],
      callbacks,
    }: UploadPapersOptions) => {
      const {
        onTaskStart,
        onProgress,
        onTaskComplete,
        onAllTasksComplete,
      } = callbacks || {};
      type UploadRecord = {
        id: string;
        fileInfo: FileInfo;
        file: File;
        uploadUrl: string;
        existed: boolean;
        userExisted: boolean;
        subjectExisted: boolean;
        code: number;
      };
      try {
        // 1. 预处理：确保我们拥有所有文件的 hash（md5+sha1），供后续匹配使用
        let computedFilesInfo: FileInfo[] = filesInfo || [];
        if (!filesInfo || filesInfo.length !== files.length) {
          computedFilesInfo = [];
          for (const file of files) {
            computedFilesInfo.push(await getFileInfo(file));
          }
        }
        const finalFilesInfo = computedFilesInfo;
        const existFormData = new FormData();
        finalFilesInfo.forEach((data, i) => {
          existFormData.append(`files[${i}][md5]`, data.md5);
          existFormData.append(`files[${i}][sha1]`, data.sha1);
        });
        existFormData.append('subject_id', subjectId);

        // 2. 告知服务端需要上传哪些文件，并拿到 OSS 上传 URL / existed 标记
        const existData = (
          await fun.post<WrapResponse<UploadExistDataItem[]>>(
            api('/paper/upload/exist', true),
            existFormData,
          )
        ).data;

        if (existData.code !== 0) {
          toast.error(existData.message);
          return;
        }

        const existDataList: UploadExistDataItem[] = snakeToCamel<UploadExistDataItem[]>(existData.data) ?? [];
        const uploads: UploadRecord[] = [];
        const seenHashesInBatch = new Set<string>();
        const pdfMaxSize = get().globalConfig.value?.pdfMaxSize || MAX_SIZE_M;

        // 若后端返回数量少于请求数量，为「缺失」的任务主动调用 onTaskComplete，避免面板卡在上传中
        if (existDataList.length < files.length) {
          for (let i = existDataList.length; i < files.length; i++) {
            const taskId = taskIds[i] ?? finalFilesInfo[i]?.id;
            if (taskId) {
              onProgress?.(taskId, 100);
              onTaskComplete?.(taskId, {
                code: -2,
                paper: null,
                isDuplicate: false,
              });
            }
          }
        }

        existDataList.forEach(({
          existed, uploadUrl = '', userExisted, subjectExisted = false,
        }, index) => {
          const fileInfo = finalFilesInfo[index];
          const file = files[index];
          const taskId = taskIds[index] ?? fileInfo?.id;
          if (!fileInfo || !file) {
            if (taskId) {
              onProgress?.(taskId, 100);
              onTaskComplete?.(taskId, {
                code: -2,
                paper: null,
                isDuplicate: false,
              });
            }
            return;
          }
          const overSize = file.size > (pdfMaxSize * 1024 * 1024);
          // 本次上传已重复
          const isDuplicateInBatch = seenHashesInBatch.has(fileInfo.id);
          if (!isDuplicateInBatch) {
            seenHashesInBatch.add(fileInfo.id);
          }

          let code = 0;
          if (isDuplicateInBatch) {
            code = -3;
          } else if (userExisted) {
            // 在主题中：根据 subjectExisted 区分「已同步当前主题」(-5) 与「已在当前主题内」(-6)
            if (subjectId) {
              code = subjectExisted ? -6 : -5;
            } else {
              code = -4;
            }
          } else if (overSize) {
            code = -10;
          }

          const record: UploadRecord = {
            id: taskId,
            fileInfo,
            file,
            uploadUrl,
            existed,
            userExisted,
            subjectExisted,
            code,
          };

          uploads.push(record);
          onTaskStart?.(record.id, file);
        });

        let balanceInsufficient = false;
        let uploadLimitReached = false;
        // 3. 针对每个待上传文件：必要时推送到 OSS，再调用 bindUploadByHash
        for (const upload of uploads) {
          const { id, file, uploadUrl, fileInfo, existed, code } = upload;
          try {
            if (code === -10) {
              continue;
            }

            if (!existed && code !== -3 && code !== -4 && code !== -5 && code !== -6) {
              const max = Math.floor(Math.random() * (99 - 93 + 1) + 93);
              await axios.put<WrapResponse<Paper[]>>(
                uploadUrl,
                file,
                {
                  headers: {
                    'Content-Type': 'application/pdf',
                  },
                  onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded / (progressEvent.total || 1)) * max);
                    onProgress?.(id, Math.min(progress, 99));
                  },
                }
              );
            }

            // 添加超时机制，防止 bindUploadByHash 无限期卡住
            let binded = null;
            try {
              const bindPromise = bindUploadByHash(
                subjectId,
                [fileInfo],
                ignoreAiCol,
              );
              const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('bindUploadByHash timeout')), 10000); // 10秒超时
              });
              binded = await Promise.race([bindPromise, timeoutPromise]) as any;
            } catch (bindError) {
              console.error('Error in bindUploadByHash:', bindError);
              // bindUploadByHash 失败时，仍然调用 onTaskComplete，避免任务卡住
              onTaskComplete?.(id, {
                code: -2,
                paper: null,
                isDuplicate: false,
              });
              onProgress?.(id, 100);
              continue;
            }

            if (binded?.code === 5001) {
              balanceInsufficient = true;
            }
            if (binded?.code === 30009) {
              uploadLimitReached = true;
            }

            // 非重复的情况，刷新列表，主题除外
            if (![-3, -4, -5, -6].includes(code) || !!subjectId) {
              // 添加超时机制，防止 fetchPapers 无限期卡住
              try {
                const fetchPromise = get().fetchPapers(subjectId, true);
                const timeoutPromise = new Promise((_, reject) => {
                  setTimeout(() => reject(new Error('fetchPapers timeout')), 30000); // 30秒超时
                });
                await Promise.race([fetchPromise, timeoutPromise]);
              } catch (fetchError) {
                console.error('Error in fetchPapers:', fetchError);
                // fetchPapers 失败不影响任务完成，继续执行
              }
            }

            // 4. 根据 bind 结果，通过回调通知外部任务状态
            if (code === -3 || code === -4 || code === -5 || code === -6) {
              onTaskComplete?.(id, {
                code,
                paper: binded?.data?.[0] ?? null,
                isDuplicate: true,
              });
            } else if (binded?.code === 0) {
              onTaskComplete?.(id, {
                code: 0,
                paper: binded.data?.[0] ?? null,
                isDuplicate: false,
              });
            } else {
              onTaskComplete?.(id, {
                code: binded?.code ?? -2,
                paper: null,
                isDuplicate: false,
              });
            }
            onProgress?.(id, 100);
          } catch (error) {
            console.error('Error uploading file:', error);
            onTaskComplete?.(id, {
              code: -2,
              paper: null,
              isDuplicate: false,
            });
          } finally {
            get().refreshUserInfo();
          }
        }

        onAllTasksComplete?.({ balanceInsufficient, uploadLimitReached });
        if (balanceInsufficient) {
          get().setBalanceInsufficient(true);
        }
        get().fetchPaperCols(subjectId);
        await get().fetchPapers(subjectId, true);
      } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
      }
    },

    deletePaper: async (paperId, subjectId) => {
      try {
        const formData = new FormData();
        formData.append('paper_id', paperId);
        const { code, message } = (await fun.post<WrapResponse<{}>>(
          api('/paper/edit/delete', true),
          formData,
        )).data;
        if (code === 0) {
          set((state) => {
            const data = state.papersData[pid(subjectId)];
            data.papers = data.papers.filter((paper) => paper.paperId !== paperId);
            data.total = data.total - 1;
          });
          get().fetchPaperCols(subjectId)
          get().fetchPapers(subjectId);
          get().refreshUserInfo();
          return true;
        } else {
          toast.error(message);
        }
        return false;
      } catch (error) {
        console.error('Error delete paper:', error);
        return false;
      } finally {

      }
    },
    deletePapers: async (paperIds, subjectId) => {
      try {
        const formData = new FormData();
        paperIds.forEach((paperId, index) => {
          formData.append(`paper_id[${index}]`, paperId);
        });
        const { code, message } = (await fun.post<WrapResponse<{}>>(
          api('/paper/edit/delete', true),
          formData,
        )).data;
        if (code === 0) {
          set((state) => {
            const data = state.papersData[pid(subjectId)];
            data.papers = data.papers.filter((paper) => !paperIds.includes(paper.paperId));
            data.total = data.total - paperIds.length;
          });
          get().fetchPapers(subjectId);
          get().fetchPaperCols(subjectId)
          get().refreshUserInfo();
          return true;
        } else {
          toast.error(message);
        }
        return false;
      } catch (error) {
        console.error('Error delete papers:', error);
        return false;
      } finally {

      }
    },
    fetchPaperCols: async (subjectId) => {
      try {
        const formData = new FormData();
        if (subjectId) {
          formData.append('subject_id', subjectId);
        }
        const { data, code, message } = (await fun.post<WrapResponse<ColData[]>>(
          api('/paper/aicol/getConf', true),
          formData,
        )).data;
        if (code === 0) {
          const cols = colDataToCol(snakeToCamel<ColData[]>(data));
          set((state) => {
            state.papersData[pid(subjectId)].cols = cols;
          });
        } else {
          toast.error(message);
        }
      } catch (error) {
        console.error('Error delete papers:', error);
      } finally {

      }
    },
    setCitationStyle: async (styleKey) => {
      try {
        const { code, message } = (await fun.post<WrapResponse<{}>>(
          api('/paper/aicol/setCitationStyle', true),
          { style_key: styleKey },
        )).data;
        if (code !== 0) {
          toast.error(message || '引用格式设置失败');
          return false;
        }
        persistListCitationStyleKey('', styleKey);
        set((state) => {
          const storeKey = COMMON;
          const prev = state.papersData[storeKey];
          if (prev) {
            state.papersData[storeKey] = {
              ...prev,
              citationStyleKey: styleKey,
            };
          }
        });
        return true;
      } catch (error) {
        console.error('Error set citation style:', error);
        toast.error('引用格式设置失败');
        return false;
      }
    },
    setPaperCols: async (cols, subjectId, mode) => {
      try {
        toast.loading('AI 列设置中');
        const formData = new FormData();
        cols.forEach((col, index) => {
          const key = transferCamelToSnake(col);
          formData.append(`cols[${index}]`, key);
        });
        subjectId && formData.append('subject_id', subjectId);
        mode && formData.append('mode', mode);
        const { code, message } = (await fun.post<WrapResponse<{}>>(
          api('/paper/aicol/set', true),
          formData,
        )).data;
        if (code === 0) {
          set((state) => {
            const { cols: stateCols } = state.papersData[pid(subjectId)];
            state.papersData[pid(subjectId)].cols = stateCols.map((col) => ({
              ...col,
              custom: cols.includes(col.key) ? 1 : 0,
            }));
          });
          get().refreshUserInfo();
          toast.dismiss();
          toast.success('AI 列设置成功');
        } else if (code === 5001) {
          let payment = 0;
          get().papersData[pid(subjectId)].cols.forEach((col) => {
            if (cols.includes(col.key)) {
              payment += col.cost;
            }
          });
          get().setBalanceInsufficient(payment > 0 ? payment : true);
        } else {
          toast.dismiss();
          toast.error(message);
        }
        get().fetchPaperCols(subjectId);
        await get().fetchPapers(subjectId);
      } catch (error) {
        console.error('Error set paper cols:', error);
      }
    },
    colConsumePrice: null,
    setColConsumePrice: (colConsumePrice) => {
      set((state) => {
        state.colConsumePrice = colConsumePrice;
      });
    },
    refreshPaperCol: async (key, subjectId, paperId, mode) => {
      try {
        const params = {} as any;
        if (key) {
          params['col_key'] = transferCamelToSnake(key);
        }
        if (paperId) {
          params['paper_id'] = paperId;
        }
        if (subjectId) {
          params['subject_id'] = subjectId;
        }
        if (mode) {
          params['mode'] = mode;
        }
        const { data, code, message } = (await fun.post<WrapResponse<{}>>(
          api('/paper/aicol/refresh', true),
          params,
        )).data;
        if (code === 0) {
          // 只是刷数据消耗
          if (mode === 'budget') {
            const colConsumePrice = snakeToCamel(data) as ColConsumePrice;
            set((state) => {
              state.colConsumePrice = colConsumePrice;
            });
            return colConsumePrice;
          }
          // 执行刷新
          if (key) {
            set(state => {
              const data = state.papersData[pid(subjectId)];
              const index = data.cols.findIndex((col) => col.key === key);
              data.cols[index].expired = 0;
            });
          }
          get().fetchPaperCols(subjectId)
          get().refreshUserInfo();
          setTimeout(async () => {
            await get().fetchPapers(subjectId);
          }, 200);
          toast.success('AI 表格列已刷新');
        } else if (code === 5001) {
          get().setBalanceInsufficient(true);
        } else {
          toast.error(message);
        }
      } catch (error) {
        console.error('Error set paper cols:', error);
      }
    },
    preCreateAiCol: async (options) => {
      try {
        const payload = Object.entries(options).reduce((result, [key, value]) => {
          result[transferCamelToSnake(key)] = value;
          return result;
        }, {} as Record<string, unknown>);
        const { data, code, message } = (await fun.post<WrapResponse<Record<string, unknown>>>(
          api('/paper/aicol/preCreate', true),
          payload,
        )).data;
        if (code === 0) {
          return snakeToCamel(data) as PreCreateAiColResult;
        }
        toast.error(message);
        return null;
      } catch (error) {
        console.error('Error pre create ai col:', error);
        return null;
      }
    },
    createAiCol: async (options, subjectId) => {
      toast.loading('自定义 AI 列创建中');
      try {
        const payload = Object.entries(options).reduce((result, [key, value]) => {
          result[transferCamelToSnake(key)] = value;
          return result;
        }, {} as Record<string, unknown>);
        const { code, message } = (await fun.post<WrapResponse<{}>>(
          api('/paper/aicol/create', true),
          payload,
        )).data;
        if (code === 0) {
          await get().fetchPaperCols(subjectId);
          toast.dismiss();
          toast.success('自定义 AI 列创建成功');
        } else {
          toast.dismiss();
          toast.error(message);
        }
      } catch (error) {
        console.error('Error create ai col:', error);
      } finally {
      }
    },
    deleteAiCol: async (options, subjectId) => {
      toast.loading('自定义 AI 列删除中');
      try {
        const { code, message } = (await fun.post<WrapResponse<{}>>(
          api('/paper/aicol/delete', true),
          {
            col_id: options.colId,
          },
        )).data;
        if (code === 0) {
          await get().fetchPaperCols(subjectId);
          toast.dismiss();
          toast.success('自定义 AI 列删除成功');
        } else {
          toast.dismiss();
          toast.error(message);
        }
      } catch (error) {
        console.error('Error create ai col:', error);
      } finally {
      }
    },
    referencesExplain: async (paperId) => {
      try {
        const formData = new FormData();
        formData.append('paper_id', paperId);
        const { code, message } = (await fun.post<WrapResponse<{}>>(
          api('/paper/references/explain', true),
          formData,
        )).data;
        if (code === 0) {
          await get().fetchPaper(paperId, true);
          toast.success('已开始 AI 提取参考文献');
          get().refreshUserInfo();
        } else if (code === 5001) {
          get().setBalanceInsufficient(true);
        } else {
          toast.error(message);
        }
      } catch (error) {
        console.error('Error explain references:', error);
      }
    },
    refreshPaper: async (paperId) => {
      try {
        await get().fetchPaper(paperId, true);
        toast.success('刷新成功');
      } catch (error) {
        console.error('Error refresh paper:', error);
      }
    },
    updateReadingProgress: async (params) => {
      try {
        const formData = new FormData();
        formData.append('paper_id', params.paperId);
        formData.append('curr_page', params.currPage);
        formData.append('total_page', params.totalPage);
        await fun.post<WrapResponse<{}>>(
          api('/paper/detail/update_reading_progress', true),
          formData,
        );
      } catch (error) {
        console.error('Error update reading progress:', error);
      }
    },
    fetchPaperMetadataLoading: false,
    clearCurrentPaperMetadataByDoi: () => {
      set(state => {
        state.currentPaperMetadataByDoi = null;
        state.currentPaperMetadataByDoiError = '';
      });
    },
    currentPaperMetadataByDoi: null,
    currentPaperMetadataByDoiError: '',
    fetchPaperMetadataByDoi: async ({
      doi,
    }) => {
      set(state => {
        state.fetchPaperMetadataLoading = true;
        state.currentPaperMetadataByDoi = null;
        state.currentPaperMetadataByDoiError = '';
      });
      try {
        const { data, code, message } = (
          await fun.post<WrapResponse<PaperMetadataData>>(
            api('/paper/metadata/getByDoi', true),
            {
              doi,
            },
          )
        ).data;
        const paperData = snakeToCamel<PaperMetadataData>(data);
        if (code === 0) {
          set((state) => {
            state.currentPaperMetadataByDoi = Object.keys(paperData)
              .reduce((acc, key) => {
                const item = paperData[key as keyof PaperMetadataData];
                if (Array.isArray(item)) {
                  return {
                    ...acc,
                    [key]: item.join(','),
                  }
                } else {
                  return {
                    ...acc,
                    [key]: item,
                  };
                }
              }, {} as Partial<PaperMetadata>) as PaperMetadata;
            state.currentPaperMetadataByDoiError = '';
          });
        } else {
          set(state => {
            state.currentPaperMetadataByDoi = null;
            state.currentPaperMetadataByDoiError = message;
          });
        }
      } catch (error) {
        console.error('Error fetch paper:', error);
        set(state => {
          state.currentPaperMetadataByDoi = null;
          state.currentPaperMetadataByDoiError = '服务端请求失败，请重试';
        });
      } finally {
        set(state => {
          state.fetchPaperMetadataLoading = false;
        });
      }
    },
    savePaperMetadata: async ({
      paperId,
      metadata,
    }) => {
      try {
        toast.loading('保存中...');
        const { data, code, message } = (
          await fun.post<WrapResponse<PaperMetadataData>>(
            api('/paper/metadata/setData', true),
            snakeToCamel<SavePaperMetadataOptions, false>({
              paperId,
              metadata: {
                ...metadata,
                year: Number(metadata.year) || '',
              },
            }, {
              reverse: true,
            }),
          )
        ).data;
        toast.dismiss();
        if (code === 0) {
          toast.success('保存成功');
          return true;
        } else {
          toast.error(message);
          return false;
        }
      } catch (error) {
        console.error('Error fetch paper:', error);
        return false;
      } finally {
        get().clearCurrentPaperMetadataByDoi();
      }
    },
    exportPapers: async ({
      colKeys = [],
      paperIds = [],
      subjectId = '',
      format,
      fileType,
    }) => {
      try {
        const response = (
          await fun.post(
            api('/paper/export', true),
            {
              col_keys: colKeys.map(key => transferCamelToSnake(key)),
              paper_id: paperIds,
              subject: subjectId,
              format,
              file_type: fileType,
            },
            {
              responseType: 'blob',
            },
          )
        );
        // 获取文件名
        let filename = `Findin文献表格_${getCurrentTime().replace(/[\s\-]+/g, '_')}.csv`;
        const disposition = response.headers['content-disposition'];
        if (disposition) {
          // 优先匹配 filename*=UTF-8''xxx
          let match = disposition.match(/filename\*\=UTF-8''([^;]+)/i);
          if (match) {
            filename = decodeURIComponent(match[1]);
          }
        }
        return {
          filename,
          data: response.data,
        }
      } catch (error) {
        console.error('Error export papers:', error);

        return null;
      } finally {
        get().resetRowSelection();
      }
    },
  });
});


export const requestPapers = async (
  params: FetchPapersParams & { signal?: AbortSignal },
  papersData: PapersData,
): Promise<PapersData | null> => {
  try {
    // 从 params 中提取 signal，同时确保不将它发送到后端
    const { signal, ...requestParams } = params || {};
    const { subjectId, mode, ...restParams } = requestParams || {};
    const requestData = snakeToCamel<FetchPapersParams, false>({
      ...requestParams,
    }, {
      reverse: true,
    });

    const { data, code, message } = (
      await fun.post<WrapResponse<FetchPapersData>>(
        api('/paper/list', true),
        requestData,
        {
          signal, // 传递中止信号给 axios
        }
      )
    ).data;

    // 检查请求是否已被中止
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError'); // 主动抛出中止错误
    }

    const listPayload = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
    const camelData = snakeToCamel<FetchPapersData>(
      listPayload as DeepCamelToSnake<FetchPapersData>,
      {
        modifier: (v, k) => (k?.endsWith('id') ? v.toString() : v),
      },
    ) as Record<string, unknown>;

    const paperAiCol = (camelData.paperAiCol ?? camelData.paper_ai_col ?? []) as ColData[];
    const list = (camelData.list ?? []) as Paper[];
    const {
      paperAiCol: _paperAiCol,
      paper_ai_col: _paperAiColSnake,
      list: _list,
      citationStyle: _citationStyle,
      citation_style: _citationStyleSnake,
      citationStyleKey: _citationStyleKey,
      citation_style_key: _citationStyleKeySnake,
      ...rest
    } = camelData;

    if (code === 0) {
      const cols: Col[] = colDataToCol(paperAiCol);
      const papers: Paper[] = list.map((p) => ({
        ...p,
        subjects: (p.subjects || []),
      }));

      const hasCitationInResponse =
        'citation_style' in listPayload || 'citationStyle' in listPayload;
      const extractedCitationStyleKey =
        extractListCitationStyleKey(camelData)
        ?? extractListCitationStyleKey(listPayload);
      const citationStyleKey =
        extractedCitationStyleKey
        ?? (hasCitationInResponse ? null : papersData.citationStyleKey)
        ?? null;

      const citationStyleFromResponse = (
        camelData.citationStyle
        ?? camelData.citation_style
        ?? listPayload.citationStyle
        ?? listPayload.citation_style
        ?? null
      ) as PapersCitationStyle | null;
      const citationStyle = (
        citationStyleFromResponse
        ?? (hasCitationInResponse ? null : papersData.citationStyle)
        ?? null
      ) as PapersCitationStyle | null;

      return {
        ...papersData,
        papers,
        cols,
        ...restParams,
        ...rest,
        citationStyle,
        citationStyleKey,
        citationStyleFromListResponse: hasCitationInResponse,
      };
    }
    toast.error(message);
    return null;
  } catch (error) {
    // 让中止错误继续冒泡
    if (isAbortError(error)) {
      throw error;
    }
    console.error('Error fetch paper:', error);
    return null;
  }
};

// 辅助函数：判断是否为中止错误
function isAbortError(error: unknown): error is { name: string } {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (typeof error === 'object' && error !== null && 'name' in error && error.name === 'CanceledError')
  );
}
